import moment from 'moment';
import { CacheCallback } from './CacheCallback';

export abstract class Cache<T> {
  constructor(ttlMs?: number) {
    this.ttlMs = ttlMs || moment.duration(1, 'days').asMilliseconds();
  }

  private isAlreadyFetching = false;
  private lastCacheUpdateTs = 0;
  readonly ttlMs: number;
  private cacheCallbacks: CacheCallback<T>[] = [];
  private cachedData?: T = undefined;

  private onFetchSuccess(data: T) {
    this.cachedData = data;
    this.lastCacheUpdateTs = Date.now();
    this.cacheCallbacks = this.cacheCallbacks.filter((callback) =>
      callback.onSuccess(data)
    );
    this.isAlreadyFetching = false;
  }

  private onFetchError(error: unknown) {
    this.cacheCallbacks = this.cacheCallbacks.filter((callback) =>
      callback.onError(error)
    );
    this.isAlreadyFetching = false;
  }

  private addFetchCallback(callback: CacheCallback<T>) {
    this.cacheCallbacks.push(callback);
  }

  abstract fetchData(): Promise<T>;

  getData(): Promise<T> {
    if (Date.now() - this.lastCacheUpdateTs < this.ttlMs && this.cachedData) {
      return Promise.resolve(this.cachedData);
    }
    if (!this.isAlreadyFetching) {
      this.isAlreadyFetching = true;
      this.cachedData = undefined;
      this.fetchData()
        .then((data) => this.onFetchSuccess(data))
        .catch((error) => this.onFetchError(error));
    }
    return new Promise((resolve) => {
      this.addFetchCallback({
        onSuccess: (data) => resolve(data),
        onError: (error) => resolve(Promise.reject(error)),
      });
    });
  }

  private doInvalidateCache() {
    this.lastCacheUpdateTs = 0;
    this.cachedData = undefined;
  }

  invalidate() {
    if (this.isAlreadyFetching) {
      this.addFetchCallback({
        onSuccess: () => this.doInvalidateCache(),
        onError: () => this.doInvalidateCache(),
      });
    } else {
      this.doInvalidateCache();
    }
  }
}
